import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { dropTargetAt, rememberDrop, setDragging } from './dropTargets';
import { DRAG, gestureIntent, scrollsSideways } from './gestureIntent';

/** How far a card moves before it lifts. */
const DRAG_START_PX = 6;
/** How long a card that can't be played says why, after you try to drag it. */
const REFUSED_MS = 2200;
/** How long a dropped card waits where it was let go for the game to take it, before going back. */
const DROP_WAIT_MS = 2500;
/** The dragged card is drawn a little bigger than where it was, as if lifted. */
const LIFT = 'scale(1.06)';

/** Reads a touch's direction once it's far enough to tell, from whichever of its events arrives first. */
const decide = (g, x, y, timeStamp) => {
  if (g.claimed === undefined) {
    const intent = gestureIntent({ dx: x - g.x, dy: y - g.y, heldMs: timeStamp - g.at, handScrolls: g.handScrolls });
    g.claimed = intent === null ? undefined : intent === DRAG;
  }
};

/**
 * Dragging a card (from the hand, or a Pokémon in play) onto a lit spot on the board. A tap still taps: a click right
 * after a drag is swallowed, nothing else is.
 *
 * Starting a drag calls `lift`, which starts whatever flow lights the spots up and returns what's being dragged
 * (setDragging, which the spots read). Letting go on a lit spot does what tapping it would (dropTargetAt). The card
 * then waits where it was let go until the game takes it (it unmounts, or `settledBy` changes) so it doesn't fly
 * there again (rememberDrop); a spot whose tap asks something first returns false, and the card goes back at once.
 * Letting go anywhere else calls `onMiss`. While it's lifted, what was dragged is hidden from its place, unless it
 * `staysWhileLifted` (a card drawn off the deck leaves the deck there).
 *
 * A card that can't be played now (`refusal`: why not) doesn't lift: trying to drag it returns `refused`, the
 * reason, for a moment, so the card can shake and say why instead of doing nothing.
 *
 * The finger is followed outside React (the lifted card is moved directly, once a frame), and a touch is claimed in
 * its first few pixels (gestureIntent), so the phone's browser doesn't take it for a scroll.
 *
 * @returns {{ bind: object, ghost: function, refused: ?string }} props for the element that's dragged, a function that
 *   draws the lifted card (given what the element shows) while it's lifted, and why it can't be dragged, just after
 *   a try
 */
export default function useCardDrag({
  enabled, place, lift, onMiss, settledBy, refusal = null, touchAction = 'none', staysWhileLifted = false,
}) {
  const ref = useRef(null);
  const ghostRef = useRef(null);
  const gesture = useRef(null);
  const suppressClick = useRef(false);
  const latest = useRef(null);
  latest.current = { place, lift, onMiss, enabled, refusal };
  const [refused, setRefused] = useState(null);
  useEffect(() => {
    if (!refused) {
      return undefined;
    }
    const timer = setTimeout(() => setRefused(null), REFUSED_MS);
    return () => clearTimeout(timer);
  }, [refused]);
  // Where the lifted card starts (the card's place on screen); null when nothing is lifted.
  const [lifted, setLifted] = useState(null);
  const [waitingForGame, setWaitingForGame] = useState(false);

  useEffect(() => {
    if (!waitingForGame) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setWaitingForGame(false);
      setLifted(null);
    }, DROP_WAIT_MS);
    return () => clearTimeout(timer);
  }, [waitingForGame]);

  // The game answered a drop: what was dragged is somewhere else now. (Not mid-drag: an update from the opponent
  // mustn't take the card out from under the finger.)
  const waitingRef = useRef(false);
  waitingRef.current = waitingForGame;
  useEffect(() => {
    if (waitingRef.current) {
      setWaitingForGame(false);
      setLifted(null);
    }
  }, [settledBy]);

  // Touch: once the card is claimed, nothing scrolls until the finger lifts.
  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return undefined;
    }
    const touchMove = (event) => {
      const g = gesture.current;
      const touch = event.touches[0];
      if (!g || !touch) {
        return;
      }
      decide(g, touch.clientX, touch.clientY, event.timeStamp);
      if (g.claimed && event.cancelable) {
        event.preventDefault();
      }
    };
    element.addEventListener('touchmove', touchMove, { passive: false });
    return () => element.removeEventListener('touchmove', touchMove);
  }, [enabled, refusal]);

  useEffect(() => {
    const follow = () => {
      const g = gesture.current;
      if (g) {
        g.frame = null;
        if (ghostRef.current) {
          ghostRef.current.style.transform = `translate(${g.dx}px, ${g.dy}px) ${LIFT}`;
        }
      }
    };

    const move = (event) => {
      const g = gesture.current;
      if (!g || g.id !== event.pointerId) {
        return;
      }
      g.dx = event.clientX - g.x;
      g.dy = event.clientY - g.y;

      if (!g.dragging) {
        decide(g, event.clientX, event.clientY, event.timeStamp);
        if (g.claimed === false) {
          gesture.current = null; // scrolling through the hand
          return;
        }
        if (!g.claimed || Math.hypot(g.dx, g.dy) <= DRAG_START_PX) {
          return;
        }
        if (!latest.current.enabled) {
          // Can't be played now: say why rather than lift it.
          gesture.current = null;
          suppressClick.current = true;
          setTimeout(() => { suppressClick.current = false; }, 0);
          setRefused(latest.current.refusal);
          return;
        }
        g.dragging = true;
        suppressClick.current = true;
        setLifted(g.rect);
        setDragging(latest.current.lift());
      }
      if (g.frame === null) {
        g.frame = requestAnimationFrame(follow);
      }
    };

    const end = (event, cancelled) => {
      const g = gesture.current;
      if (!g || g.id !== event.pointerId) {
        return;
      }
      gesture.current = null;
      if (!g.dragging) {
        return;
      }
      cancelAnimationFrame(g.frame);
      // The click that follows a drag comes right after this, if at all (not when it ended over something else),
      // so the next real tap isn't swallowed.
      setTimeout(() => { suppressClick.current = false; }, 0);

      // The innermost place under the finger that takes the card: dropping on a card in your hand is dropping on
      // your hand.
      const under = cancelled ? null : document.elementFromPoint(event.clientX, event.clientY);
      let anchor = under && under.closest('[data-anchor]');
      let commit = null;
      while (anchor && !(commit = dropTargetAt(anchor.dataset.anchor))) {
        anchor = anchor.parentElement && anchor.parentElement.closest('[data-anchor]');
      }
      setDragging(null);

      if (!commit) {
        setLifted(null);
        if (latest.current.onMiss) {
          latest.current.onMiss();
        }
        return;
      }
      if (ghostRef.current) {
        ghostRef.current.style.transform = `translate(${g.dx}px, ${g.dy}px) ${LIFT}`;
      }
      if (commit() === false) {
        setLifted(null); // it asks something first (a menu): the card goes back while you answer
        return;
      }
      rememberDrop(latest.current.place);
      setWaitingForGame(true);
    };

    const up = (event) => end(event, false);
    const cancel = (event) => end(event, true);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
    };
  }, []);

  const onPointerDown = (event) => {
    if ((!enabled && !refusal) || (event.pointerType === 'mouse' && event.button !== 0)) {
      return;
    }
    gesture.current = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      dx: 0,
      dy: 0,
      at: event.timeStamp,
      rect: event.currentTarget.getBoundingClientRect(),
      // A mouse drags in every direction; a finger's first few pixels decide (decide).
      claimed: event.pointerType === 'mouse' ? true : undefined,
      handScrolls: scrollsSideways(event.currentTarget),
      dragging: false,
      frame: null,
    };
  };

  const onClickCapture = (event) => {
    if (suppressClick.current) {
      suppressClick.current = false;
      event.stopPropagation();
      event.preventDefault();
    }
  };

  const bind = {
    ref,
    onPointerDown,
    onClickCapture,
    // Holding a card mustn't select text or bring up the iPhone's callout.
    style: {
      touchAction: enabled || refusal ? touchAction : undefined,
      userSelect: 'none',
      WebkitUserSelect: 'none',
      WebkitTouchCallout: 'none',
      visibility: lifted && !staysWhileLifted ? 'hidden' : undefined,
    },
  };

  const ghost = (face) => lifted && createPortal(
    <div
      ref={ghostRef}
      style={{
        position: 'fixed',
        left: lifted.left,
        top: lifted.top,
        zIndex: 41,
        pointerEvents: 'none',
        willChange: 'transform',
        transform: LIFT,
        '--card-w': `${lifted.width}px`,
        filter: 'drop-shadow(0 8px 14px rgba(0, 0, 0, .45))',
      }}
      aria-hidden="true"
    >
      {face}
    </div>,
    document.body,
  );

  return { bind, ghost, refused };
}
